import React, { useMemo } from "react";
import { defineChart, dot, lineY, text } from "@tanstack/charts";
import { decorative } from "@tanstack/charts/mark/decorative";
import { scaleLinear } from "@tanstack/charts/scales/linear";
import { tooltip } from "@tanstack/charts/tooltip";

import { ChartFrame } from "./ChartFrame";

export interface ScatterPoint {
    name: string;
    x: number;
    y: number;
    color?: string;
}

export function ScatterChart({
    points,
    xFormat,
    yFormat,
    xLabel,
    yLabel,
    xDomain,
    yDomain,
    connect = false,
    ariaLabel,
    height = 320,
    width,
    style,
    className
}: {
    points: ScatterPoint[];
    xFormat?: (value: number) => string;
    yFormat?: (value: number) => string;
    xLabel?: string;
    yLabel?: string;
    xDomain?: [number, number];
    yDomain?: [number, number];
    connect?: boolean;
    ariaLabel: string;
    height?: number;
    width?: number;
    style?: React.CSSProperties;
    className?: string;
}): React.ReactElement {
    const definition = useMemo(() => {
        const marks = [];
        if (connect && points.length > 1) {
            marks.push(decorative(lineY(points, { x: "x", y: "y", stroke: points[0]?.color || "#2186c5", strokeWidth: 2 })));
        }
        marks.push(
            dot(points, {
                x: "x",
                y: "y",
                key: "name",
                r: 7,
                color: (point) => point.color || "#2186c5"
            })
        );
        marks.push(
            decorative(
                text(points, {
                    x: "x",
                    y: "y",
                    text: "name",
                    dy: -12,
                    anchor: "middle",
                    fill: "#334155",
                    fontSize: 10
                })
            )
        );
        const pointColors = points.map((point) => point.color || "#2186c5");
        return defineChart({
            marks,
            color: {
                domain: pointColors,
                range: pointColors
            },
            scales: {
                x: {
                    scale: xDomain ? scaleLinear().domain(xDomain) : scaleLinear,
                    nice: !xDomain,
                    grid: true,
                    axis: { label: xLabel, ticks: xFormat ? { format: xFormat } : undefined }
                },
                y: {
                    scale: yDomain ? scaleLinear().domain(yDomain) : scaleLinear,
                    nice: !yDomain,
                    grid: true,
                    axis: { label: yLabel, ticks: yFormat ? { format: yFormat } : undefined }
                }
            },
            tooltip
        });
    }, [connect, points, xDomain, xFormat, xLabel, yDomain, yFormat, yLabel]);

    if (points.length === 0) return <div />;

    return (
        <ChartFrame
            definition={definition}
            ariaLabel={ariaLabel}
            ariaDescription={points.map((point) => point.name).join(", ")}
            height={height}
            width={width}
            style={style}
            className={className}
        />
    );
}
